import { Stroke, Style } from "ol/style";
import CircleStyle from "ol/style/Circle";
import { useEffect, useMemo, useRef, useState } from "react";
import { Col, Row } from "react-bootstrap";
import { useLocation } from "react-router-dom";
import PARAMETRE from "../../../enums/ParametreEnum.tsx";
import url from "../../../module/fetch.tsx";
import CreatePei from "../../../pages/Pei/CreatePei.tsx";
import UpdatePei from "../../../pages/Pei/UpdatePei.tsx";
import Visite from "../../../pages/Visite/Visite.tsx";
import PageTitle from "../../Elements/PageTitle/PageTitle.tsx";
import { useGet } from "../../Fetch/useFetch.tsx";
import { IconPei } from "../../Icon/Icon.tsx";
import { TypeModuleRemocra } from "../../ModuleRemocra/ModuleRemocra.tsx";
import MapComponent, { useMapComponent } from "../Map.tsx";
import { optimizeVectorLayer } from "../MapPerformanceUtils.tsx";
import { useToolbarContext } from "../MapToolbar.tsx";
import { createPointLayer } from "../MapUtils.tsx";
import MapToolbarPei, { useToolbarPeiContext } from "./MapToolbarPei.tsx";

const MapPei = () => {
  const mapElement = useRef<HTMLDivElement>();

  const [showFormPei, setShowFormPei] = useState(false);
  const [showFormVisite, setShowFormVisite] = useState({
    peiId: null,
    show: false,
  });
  const [coordonneesPeiCreate, setCoordonneesPeiCreate] = useState(null);

  const [peiIdUpdate, setPeiIdUpdate] = useState(null);

  const location = useLocation();
  const [estSurligne, setEstSurligne] = useState(
    location.state?.listePeiId?.length === 0 ||
      location.state?.listePeiId == null,
  );

  const parametres = useGet(
    url`/api/parametres?${{
      listeParametreCode: JSON.stringify([
        PARAMETRE.PEI_HIGHLIGHT_DUREE,
        PARAMETRE.PEI_HIGHLIGHT_COULEUR,
        PARAMETRE.PEI_HIGHLIGHT_RAYON,
        PARAMETRE.PEI_HIGHLIGHT_LARGEUR,
        PARAMETRE.PEI_SELECTION_COULEUR,
        PARAMETRE.PEI_SELECTION_RAYON,
        PARAMETRE.PEI_SELECTION_LARGEUR,
      ]),
    }}`,
  );

  const {
    map,
    workingLayer,
    dataPeiLayer,
    availableLayers,
    addOrRemoveLayer,
    layerListRef,
    mapToolbarRef,
    showOutilI,
    projection,
  } = useMapComponent({
    mapElement: mapElement,
    typeModule: TypeModuleRemocra.DECI,
  });

  const {
    tools: extraTools,
    createIndispoTemp,
    handleCloseIndispoTemp,
    listePeiId,
    showCreateIndispoTemp,
    listePeiTourneePrive,
    listePeiTourneeIcpe,
    listePeiTourneePublic,
    createUpdateTournee,
    handleCloseTournee,
    showCreateTournee,
    createDebitSimultane,
    handleCloseDebitSimultane,
    showCreateDebitSimultane,
    listePeiIdDebitSimultane,
    typeReseauId,
    close,
    ref,
    visible,
    visibleMove,
    refMove,
    closeMove,
    peiInfoMove,
    geometrieMove,
    deplacePei,
  } = useToolbarPeiContext({
    map,
    workingLayer,
    dataPeiLayer,
    setShowFormPei,
    setCoordonneesPeiCreate,
    coordonneesPeiCreate,
  });

  /**
   * Permet d'afficher les débits simultanés
   * @returns
   */
  const dataDebitSimultaneLayer = useMemo(() => {
    if (!map) {
      return;
    }

    const layer = createPointLayer(
      map,
      (extent, projection) =>
        `/api/debit-simultane/layer?bbox=` +
        extent.join(",") +
        "&srid=" +
        projection.getCode(),
      projection,
    );
    if (layer) {
      optimizeVectorLayer(layer);
    }

    return layer;
  }, [map, projection]);

  const { toggleTool, activeTool, disabledTool, infoOutilI, handleCloseInfoI } =
    useToolbarContext({
      availableLayers: availableLayers,
      map: map,
      workingLayer: workingLayer,
      extraTools: extraTools,
    });

  const stateListePeiId = location.state?.listePeiId;

  // On construit la couche de surbrillance optimisée
  const highlightLayer = useMemo(() => {
    if (!map || !stateListePeiId || estSurligne || !parametres.data) {
      return;
    }

    const highlightColor =
      parametres.data[PARAMETRE.PEI_HIGHLIGHT_COULEUR]?.parametreValeur ||
      "rgba(217, 131, 226, 0.7)";

    const highlightRadius =
      parseInt(
        parametres.data[PARAMETRE.PEI_HIGHLIGHT_RAYON]?.parametreValeur,
      ) ?? 16;

    const highlightWidth =
      parseInt(
        parametres.data[PARAMETRE.PEI_HIGHLIGHT_LARGEUR]?.parametreValeur,
      ) ?? 4;

    // Style optimisé avec cache
    const highlightStyle = new Style({
      image: new CircleStyle({
        radius: highlightRadius,
        stroke: new Stroke({
          color: highlightColor,
          width: highlightWidth,
        }),
      }),
    });

    const layer = createPointLayer(
      map,
      (extent, projection) =>
        url`/api/pei/hightlight/layer?bbox=` +
        extent.join(",") +
        "&srid=" +
        projection.getCode() +
        "&listePeiId=" +
        stateListePeiId,
      projection,
      highlightStyle,
    );

    // Application des optimisations de performance
    if (layer) {
      optimizeVectorLayer(layer);
      // Configuration spécifique pour la couche highlight
      layer.setZIndex(8999);
      layer.set("renderBuffer", 50);
    }

    return layer;
  }, [map, stateListePeiId, estSurligne, projection, parametres.data]);

  useEffect(() => {
    if (!map || !highlightLayer || !parametres.data) {
      return;
    }

    setTimeout(function () {
      map.removeLayer(highlightLayer);
      setEstSurligne(true);
    }, parametres.data[PARAMETRE.PEI_HIGHLIGHT_DUREE].parametreValeur * 1000);
  }, [map, highlightLayer, parametres]);

  return (
    <>
      <PageTitle title="Carte" icon={<IconPei />} />
      <Row className={"map-wrapper"}>
        <Col>
          <MapComponent
            map={map}
            availableLayers={availableLayers}
            addOrRemoveLayer={addOrRemoveLayer}
            layerListRef={layerListRef}
            mapToolbarRef={mapToolbarRef}
            mapElement={mapElement}
            showOutilI={showOutilI}
            toggleTool={toggleTool}
            activeTool={activeTool}
            outilI={infoOutilI}
            handleCloseInfoI={handleCloseInfoI}
            toolbarElement={
              mapToolbarRef.current && (
                <MapToolbarPei
                  toggleTool={toggleTool}
                  activeTool={activeTool}
                  map={map}
                  dataPeiLayer={dataPeiLayer}
                  showCreateIndispoTemp={showCreateIndispoTemp}
                  handleCloseIndispoTemp={handleCloseIndispoTemp}
                  listePeiId={listePeiId}
                  createIndispoTemp={createIndispoTemp}
                  listePeiTourneePrive={listePeiTourneePrive}
                  listePeiTourneeIcpe={listePeiTourneeIcpe}
                  listePeiTourneePublic={listePeiTourneePublic}
                  createUpdateTournee={createUpdateTournee}
                  handleCloseTournee={handleCloseTournee}
                  showCreateTournee={showCreateTournee}
                  dataDebitSimultaneLayer={dataDebitSimultaneLayer}
                  createDebitSimultane={createDebitSimultane}
                  handleCloseDebitSimultane={handleCloseDebitSimultane}
                  showCreateDebitSimultane={showCreateDebitSimultane}
                  listePeiIdDebitSimultane={listePeiIdDebitSimultane}
                  typeReseauId={typeReseauId}
                  closeModal={close}
                  refModal={ref}
                  visibleModal={visible}
                  setPeiIdUpdate={setPeiIdUpdate}
                  setShowFormPei={setShowFormPei}
                  showFormPei={showFormPei}
                  peiIdUpdate={peiIdUpdate}
                  disabledTool={disabledTool}
                  closeMove={closeMove}
                  refMove={refMove}
                  visibleMove={visibleMove}
                  peiInfoMove={peiInfoMove}
                  geometrieMove={geometrieMove}
                  setShowFormVisite={setShowFormVisite}
                  showFormVisite={showFormVisite}
                  coordonneesPeiCreate={coordonneesPeiCreate}
                  deplacePei={deplacePei}
                />
              )
            }
          />
        </Col>
        {showFormPei && (
          <Col
            xs={5}
            className="border-primary border-start border-3 overflow-y-scroll"
            style={{ maxHeight: "80vh" }}
          >
            <div className="bg-light p-2 ">
              {coordonneesPeiCreate ? (
                <CreatePei
                  coordonneesPeiCreate={coordonneesPeiCreate}
                  close={() => {
                    dataPeiLayer?.getSource()?.refresh();
                    workingLayer?.getSource()?.clear();
                    setCoordonneesPeiCreate(null);
                    setShowFormPei(false);
                  }}
                  map={map!}
                />
              ) : (
                peiIdUpdate && (
                  <UpdatePei
                    peiIdUpdate={peiIdUpdate}
                    close={() => {
                      dataPeiLayer?.getSource()?.refresh();
                      setPeiIdUpdate(null);
                      // Le setShowFormPei(false) est géré par la tooltip
                    }}
                    map={map}
                  />
                )
              )}
            </div>
          </Col>
        )}
        {showFormVisite.show && showFormVisite.peiId && (
          <Col
            xs={5}
            className="border-primary border-start border-3 overflow-y-scroll"
            style={{ maxHeight: "80vh" }}
          >
            <div className="bg-light p-2 ">
              <Visite
                peiIdCarte={showFormVisite.peiId}
                closeForm={() =>
                  setShowFormVisite({ show: false, peiId: null })
                }
                map={map}
              />
            </div>
          </Col>
        )}
      </Row>
    </>
  );
};

export default MapPei;
